import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '@environments/environment';
import type { AuthLoginRequest } from '../models/auth-login-request.model';
import type { AuthLoginResponse } from '../models/auth-login-response.model';
import type { AuthRefreshResponse } from '../models/auth-refresh-response.model';
import type { AuthSelectTenantRequest } from '../models/auth-select-tenant-request.model';
import type { AuthSelectTenantResponse } from '../models/auth-select-tenant-response.model';
import type { ForgotPasswordRequest } from '../models/forgot-password-request.model';
import type { ResetPasswordRequest } from '../models/reset-password-request.model';
import { UserSessionService } from '@core/services/user-session.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import { HttpParams } from '@angular/common/http';

const REMEMBER_ME_KEY = 'auth_login_remember_me';

export type RememberedLogin = { email: string; rememberMe: boolean };

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly session = inject(UserSessionService);
  private readonly storage = inject(LocalStorageService);

  private static readonly loginPath = '/api/auth/login';
  private static readonly selectTenantPath = '/api/auth/select-tenant';
  private static readonly refreshTokenPath = '/api/auth/refresh-token';
  private static readonly forgotPasswordPath = '/api/auth/forgot-password';
  private static readonly resetPasswordPath = '/api/auth/reset-password';
  private static readonly logoutPath = '/api/auth/logout';

  login(body: AuthLoginRequest): Observable<AuthLoginResponse> {
    const url = `${this.baseUrl}${AuthService.loginPath}`;
    return this.http.post<AuthLoginResponse>(url, body, {});
  }

  selectTenant(body: AuthSelectTenantRequest): Observable<AuthSelectTenantResponse> {
    const url = `${this.baseUrl}${AuthService.selectTenantPath}`;
    return this.http.post<AuthSelectTenantResponse>(url, body);
  }

  refreshToken(refreshToken: string): Observable<AuthRefreshResponse> {
    const url = `${this.baseUrl}${AuthService.refreshTokenPath}`;
    const params = new HttpParams().set('RefreshToken', refreshToken);
    return this.http.get<AuthRefreshResponse>(url, { params });
  }

  requestReset(body: ForgotPasswordRequest): Observable<string> {
    const url = `${this.baseUrl}${AuthService.forgotPasswordPath}`;
    return this.http.post(url, body, { responseType: 'text' });
  }

  resetPassword(body: ResetPasswordRequest): Observable<string> {
    const url = `${this.baseUrl}${AuthService.resetPasswordPath}`;
    return this.http.post(url, body, { responseType: 'text' });
  }

  logout(): Observable<void> {
    const url = `${this.baseUrl}${AuthService.logoutPath}`;
    return this.http.post(url, null, { responseType: 'text' }).pipe(map(() => void 0));
  }

  readRememberedLogin(): RememberedLogin | null {
    const rememberMe = this.storage.getItem('local', REMEMBER_ME_KEY) === 'true';
    if (!rememberMe) return null;

    const email = (this.storage.getItem('local', 'EMAIL_REMEMBER_ME_KEY') ?? '').trim();
    if (!email) return null;
    return { email, rememberMe: true };
  }

  writeRememberedLogin(email: string): void {
    this.storage.setItem('local', REMEMBER_ME_KEY, 'true');
    this.storage.setItem('local', 'EMAIL_REMEMBER_ME_KEY', email);
  }

  clearRememberedLogin(): void {
    this.storage.removeItem('local', REMEMBER_ME_KEY);
    this.storage.removeItem('local', 'EMAIL_REMEMBER_ME_KEY');
  }
}
